import api from './api';

// ============================================
// TYPES - GoFundMe Style
// ============================================

export interface Campaign {
  ID: string;
  TITULO: string;
  DESCRICAO?: string;
  HISTORIA?: string;
  IMAGEM?: string;
  IMAGEM_CAPA?: string;
  VIDEO_URL?: string;
  META_VALOR: number;
  VALOR_ARRECADADO: number;
  DATA_INICIO?: string;
  DATA_FIM?: string;
  ENTIDADE_ID?: string;
  CATEGORIA?: string;
  TAGS?: string;
  TIPO?: 'UNICA' | 'RECORRENTE' | 'EMERGENCIA' | 'MEMORIAL' | 'EQUIPA';
  VISIBILIDADE?: 'PUBLICA' | 'PRIVADA' | 'RESTRITA';
  ESTADO?: 'A' | 'I' | 'P' | 'F' | 'S' | 'C';
  // Organizador
  ORGANIZADOR_NOME?: string;
  ORGANIZADOR_EMAIL?: string;
  ORGANIZADOR_TELEFONE?: string;
  ORGANIZADOR_FOTO?: string;
  ORGANIZADOR_BIO?: string;
  // Beneficiário
  BENEFICIARIO_NOME?: string;
  BENEFICIARIO_RELACAO?: string;
  BENEFICIARIO_FOTO?: string;
  // Flags
  VERIFICADA?: boolean;
  DESTAQUE?: boolean;
  TRENDING?: boolean;
  // Contadores
  TOTAL_SHARES?: number;
  TOTAL_VIEWS?: number;
  TOTAL_COMENTARIOS?: number;
  // Configurações
  PERMITE_ANONIMO?: boolean;
  PERMITE_COMENTARIOS?: boolean;
  PERMITE_EQUIPA?: boolean;
  MOSTRA_DOADORES?: boolean;
  TAXA_PLATAFORMA?: number;
  MONTANTES_SUGERIDOS?: string;
  entidade_desig?: string;
  // Banco
  BANCO_NOME?: string;
  BANCO_IBAN?: string;
  BANCO_TITULAR?: string;
  // Meta
  CRIADO_POR?: string;
  DATA_CRIACAO?: string;
  total_doadores?: number;
  total_recompensas?: number;
}

// Input type for API calls (lowercase field names as expected by API)
export interface CampaignInput {
  titulo?: string;
  descricao?: string | null;
  imagem?: string | null;
  imagem_mime_type?: string;
  meta_valor?: number;
  data_inicio?: string | null;
  data_fim?: string | null;
  entidade_id?: string | null;
  categoria?: string;
  tipo?: string;
  visibilidade?: string;
  estado?: string;
  video_url?: string | null;
  montantes_sugeridos?: string | null;
}

export interface Donation {
  ID: string;
  CAMPANHA_ID: string;
  DOADOR_ID?: string;
  DOADOR_NOME?: string;
  DOADOR_EMAIL?: string;
  DOADOR_TELEFONE?: string;
  VALOR: number;
  MOEDA?: string;
  METODO_PAGAMENTO?: string;
  REFERENCIA_PAGAMENTO?: string;
  ANONIMO?: boolean;
  MENSAGEM?: string;
  ESTADO?: 'PENDENTE' | 'CONFIRMADO' | 'CANCELADO' | 'REEMBOLSADO';
  DATA_DONATIVO?: string;
  DATA_CONFIRMACAO?: string;
  RECIBO_EMITIDO?: boolean;
  campanha_titulo?: string;
}

export interface Reward {
  ID: string;
  CAMPANHA_ID: string;
  TITULO: string;
  DESCRICAO?: string;
  VALOR_MINIMO: number;
  QUANTIDADE_DISPONIVEL?: number;
  QUANTIDADE_RESERVADA?: number;
  IMAGEM?: string;
  DATA_ENTREGA_ESTIMADA?: string;
  ESTADO?: 'A' | 'I';
  ORDEM?: number;
  total_reivindicados?: number;
}

export interface CampaignUpdate {
  ID: string;
  CAMPANHA_ID: string;
  TITULO: string;
  CONTEUDO: string;
  IMAGEM?: string;
  VISIVEL_TODOS?: boolean;
  DATA_PUBLICACAO?: string;
  CRIADO_POR?: string;
}

export interface DonationStats {
  campanhas_ativas: number;
  campanhas_finalizadas: number;
  total_doacoes: number;
  valor_total_arrecadado: number;
  total_doadores: number;
  media_doacao: number;
}

export interface CampaignStats {
  total_doacoes: number;
  total_doadores: number;
  valor_arrecadado: number;
  media_doacao: number;
  meta: number;
  doacoes_recorrentes: number;
  dailyDonations: Array<{ data: string; quantidade: number; valor: number }>;
}

// GoFundMe Style Types
export interface Category {
  ID: string;
  CODIGO: string;
  NOME: string;
  DESCRICAO?: string;
  ICONE?: string;
  COR?: string;
  ORDEM?: number;
  ESTADO?: 'A' | 'I';
  total_campanhas?: number;
}

export interface Comment {
  ID: string;
  CAMPANHA_ID: string;
  DONATIVO_ID?: string;
  UTILIZADOR_ID?: string;
  UTILIZADOR_NOME?: string;
  UTILIZADOR_FOTO?: string;
  CONTEUDO: string;
  PARENT_ID?: string;
  LIKES?: number;
  ESTADO?: 'A' | 'P' | 'R' | 'S';
  IS_ORGANIZADOR?: boolean;
  IS_DOADOR?: boolean;
  DATA_CRIACAO?: string;
  total_respostas?: number;
  respostas?: Comment[];
}

export interface Withdrawal {
  ID: string;
  CAMPANHA_ID: string;
  VALOR: number;
  TAXA_PLATAFORMA: number;
  VALOR_LIQUIDO: number;
  BANCO_NOME?: string;
  BANCO_IBAN?: string;
  BANCO_TITULAR?: string;
  MOTIVO?: string;
  ESTADO: 'PENDENTE' | 'APROVADO' | 'PROCESSANDO' | 'CONCLUIDO' | 'REJEITADO';
  APROVADO_POR?: string;
  DATA_APROVACAO?: string;
  DATA_PROCESSAMENTO?: string;
  DATA_CONCLUSAO?: string;
  MOTIVO_REJEICAO?: string;
  COMPROVATIVO?: string;
  REFERENCIA_TRANSFERENCIA?: string;
  NOTAS_ADMIN?: string;
  DATA_CRIACAO?: string;
  campanha_titulo?: string;
  ORGANIZADOR_NOME?: string;
  ORGANIZADOR_EMAIL?: string;
}

export interface WithdrawalStats {
  pendentes: number;
  aprovados: number;
  processando: number;
  concluidos: number;
  rejeitados: number;
  total: number;
}

export interface TopDonor {
  nome: string;
  VALOR: number;
  MENSAGEM?: string;
  DATA_DONATIVO?: string;
  ANONIMO?: boolean;
}

export interface CampaignMedia {
  ID: string;
  CAMPANHA_ID: string;
  TIPO: 'IMAGEM' | 'VIDEO' | 'DOCUMENTO';
  URL: string;
  THUMBNAIL?: string;
  TITULO?: string;
  DESCRICAO?: string;
  ORDEM?: number;
  ESTADO?: 'A' | 'I';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages?: number;
  };
}

// ============================================
// SERVICE
// ============================================

class DonationsService {
  // ============================================
  // SETUP
  // ============================================

  async setupTables(): Promise<{
    success: boolean;
    message: string;
    created: string[];
    totalTables?: number;
  }> {
    const response = await api.post('/admin/donations/setup');
    return response.data;
  }

  async seedDemo(): Promise<{ success: boolean; message: string; total?: number }> {
    const response = await api.post('/admin/donations/seed-demo');
    return response.data;
  }

  async checkStatus(): Promise<{
    success: boolean;
    tables: Record<string, boolean>;
    allTablesExist: boolean;
    connectionError?: boolean;
    message?: string;
  }> {
    const response = await api.get('/admin/donations/status');
    return response.data;
  }

  async deleteDonation(id: string): Promise<void> {
    await api.delete(`/admin/donations/donations/${id}`);
  }

  async runMigrate(): Promise<{ success: boolean; migrations: { column: string; status: string; error?: string }[] }> {
    const response = await api.post('/admin/donations/migrate');
    return response.data;
  }

  // ============================================
  // CAMPAIGNS
  // ============================================

  async getCampaigns(params?: {
    page?: number;
    limit?: number;
    estado?: string;
    categoria?: string;
    tipo?: string;
  }): Promise<PaginatedResponse<Campaign>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.estado) queryParams.append('estado', params.estado);
      if (params?.categoria) queryParams.append('categoria', params.categoria);
      if (params?.tipo) queryParams.append('tipo', params.tipo);

      const response = await api.get(`/admin/donations/campaigns?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get campaigns error:', error);
      throw error;
    }
  }

  async getCampaign(id: string): Promise<Campaign & {
    rewards: Reward[];
    recentDonations: Donation[];
    updates: CampaignUpdate[];
  }> {
    try {
      const response = await api.get(`/admin/donations/campaigns/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get campaign error:', error);
      throw error;
    }
  }

  async createCampaign(data: CampaignInput): Promise<{ id: string; message: string }> {
    try {
      const response = await api.post('/admin/donations/campaigns', data);
      return response.data;
    } catch (error) {
      console.error('Create campaign error:', error);
      throw error;
    }
  }

  async updateCampaign(id: string, data: CampaignInput): Promise<{ message: string }> {
    try {
      const response = await api.put(`/admin/donations/campaigns/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update campaign error:', error);
      throw error;
    }
  }

  async deleteCampaign(id: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/admin/donations/campaigns/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete campaign error:', error);
      throw error;
    }
  }

  // ============================================
  // DONATIONS
  // ============================================

  async getDonations(params?: {
    campanha_id?: string;
    estado?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Donation>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.campanha_id) queryParams.append('campanha_id', params.campanha_id);
      if (params?.estado) queryParams.append('estado', params.estado);
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));

      const response = await api.get(`/admin/donations/donations?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get donations error:', error);
      throw error;
    }
  }

  async getDonation(id: string): Promise<Donation & { rewards: any[] }> {
    try {
      const response = await api.get(`/admin/donations/donations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get donation error:', error);
      throw error;
    }
  }

  async confirmDonation(id: string): Promise<{ message: string }> {
    try {
      const response = await api.post(`/admin/donations/donations/${id}/confirm`);
      return response.data;
    } catch (error) {
      console.error('Confirm donation error:', error);
      throw error;
    }
  }

  async cancelDonation(id: string): Promise<{ message: string }> {
    try {
      const response = await api.post(`/admin/donations/donations/${id}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Cancel donation error:', error);
      throw error;
    }
  }

  // ============================================
  // REWARDS
  // ============================================

  async getRewards(campaignId: string): Promise<{ data: Reward[] }> {
    try {
      const response = await api.get(`/admin/donations/campaigns/${campaignId}/rewards`);
      return response.data;
    } catch (error) {
      console.error('Get rewards error:', error);
      throw error;
    }
  }

  async createReward(campaignId: string, data: Partial<Reward>): Promise<{ id: string; message: string }> {
    try {
      const response = await api.post(`/admin/donations/campaigns/${campaignId}/rewards`, data);
      return response.data;
    } catch (error) {
      console.error('Create reward error:', error);
      throw error;
    }
  }

  async updateReward(id: string, data: Partial<Reward>): Promise<{ message: string }> {
    try {
      const response = await api.put(`/admin/donations/rewards/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update reward error:', error);
      throw error;
    }
  }

  async deleteReward(id: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/admin/donations/rewards/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete reward error:', error);
      throw error;
    }
  }

  // ============================================
  // UPDATES
  // ============================================

  async getUpdates(campaignId: string): Promise<{ data: CampaignUpdate[] }> {
    try {
      const response = await api.get(`/admin/donations/campaigns/${campaignId}/updates`);
      return response.data;
    } catch (error) {
      console.error('Get updates error:', error);
      throw error;
    }
  }

  async createUpdate(campaignId: string, data: Partial<CampaignUpdate>): Promise<{ id: string; message: string }> {
    try {
      const response = await api.post(`/admin/donations/campaigns/${campaignId}/updates`, data);
      return response.data;
    } catch (error) {
      console.error('Create update error:', error);
      throw error;
    }
  }

  async deleteUpdate(id: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/admin/donations/updates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete update error:', error);
      throw error;
    }
  }

  // ============================================
  // STATISTICS
  // ============================================

  async getStats(): Promise<DonationStats> {
    try {
      const response = await api.get('/admin/donations/stats');
      return response.data;
    } catch (error) {
      console.error('Get donations stats error:', error);
      throw error;
    }
  }

  async getCampaignStats(campaignId: string): Promise<CampaignStats> {
    try {
      const response = await api.get(`/admin/donations/campaigns/${campaignId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Get campaign stats error:', error);
      throw error;
    }
  }

  // ============================================
  // CATEGORIES (GoFundMe)
  // ============================================

  async getCategories(): Promise<{ data: Category[] }> {
    try {
      const response = await api.get('/admin/donations/categories');
      return response.data;
    } catch (error) {
      console.error('Get categories error:', error);
      throw error;
    }
  }

  async createCategory(data: Partial<Category>): Promise<{ id: string; message: string }> {
    try {
      const response = await api.post('/admin/donations/categories', data);
      return response.data;
    } catch (error) {
      console.error('Create category error:', error);
      throw error;
    }
  }

  // ============================================
  // COMMENTS (GoFundMe)
  // ============================================

  async getComments(campaignId: string, params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Comment>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));

      const response = await api.get(`/admin/donations/campaigns/${campaignId}/comments?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get comments error:', error);
      throw error;
    }
  }

  async deleteComment(id: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/admin/donations/comments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete comment error:', error);
      throw error;
    }
  }

  // ============================================
  // WITHDRAWALS (GoFundMe)
  // ============================================

  async getWithdrawals(params?: { estado?: string; page?: number; limit?: number }): Promise<{
    data: Withdrawal[];
    stats: WithdrawalStats;
    pagination: { page: number; limit: number };
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.estado) queryParams.append('estado', params.estado);
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));

      const response = await api.get(`/admin/donations/withdrawals?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get withdrawals error:', error);
      throw error;
    }
  }

  async getWithdrawal(id: string): Promise<Withdrawal> {
    try {
      const response = await api.get(`/admin/donations/withdrawals/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get withdrawal error:', error);
      throw error;
    }
  }

  async approveWithdrawal(id: string, notas?: string): Promise<{ message: string }> {
    try {
      const response = await api.post(`/admin/donations/withdrawals/${id}/approve`, { notas });
      return response.data;
    } catch (error) {
      console.error('Approve withdrawal error:', error);
      throw error;
    }
  }

  async rejectWithdrawal(id: string, motivo_rejeicao: string): Promise<{ message: string }> {
    try {
      const response = await api.post(`/admin/donations/withdrawals/${id}/reject`, { motivo_rejeicao });
      return response.data;
    } catch (error) {
      console.error('Reject withdrawal error:', error);
      throw error;
    }
  }

  async completeWithdrawal(id: string, data: {
    comprovativo?: string;
    referencia_transferencia?: string;
    notas?: string;
  }): Promise<{ message: string }> {
    try {
      const response = await api.post(`/admin/donations/withdrawals/${id}/complete`, data);
      return response.data;
    } catch (error) {
      console.error('Complete withdrawal error:', error);
      throw error;
    }
  }

  // ============================================
  // DISCOVERY (GoFundMe)
  // ============================================

  async searchCampaigns(params?: {
    q?: string;
    categoria?: string;
    tipo?: string;
    ordenar?: 'recentes' | 'populares' | 'quase_la' | 'novos' | 'trending';
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Campaign>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.q) queryParams.append('q', params.q);
      if (params?.categoria) queryParams.append('categoria', params.categoria);
      if (params?.tipo) queryParams.append('tipo', params.tipo);
      if (params?.ordenar) queryParams.append('ordenar', params.ordenar);
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));

      const response = await api.get(`/admin/donations/search?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Search campaigns error:', error);
      throw error;
    }
  }

  async getFeaturedCampaigns(): Promise<{
    destaque: Campaign[];
    trending: Campaign[];
    quaseLa: Campaign[];
  }> {
    try {
      const response = await api.get('/admin/donations/featured');
      return response.data;
    } catch (error) {
      console.error('Get featured campaigns error:', error);
      throw error;
    }
  }

  async getTopDonors(campaignId: string, limit?: number): Promise<{ data: TopDonor[] }> {
    try {
      const response = await api.get(`/admin/donations/campaigns/${campaignId}/top-donors${limit ? `?limit=${limit}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Get top donors error:', error);
      throw error;
    }
  }

  async getRecentDonors(campaignId: string, limit?: number): Promise<{ data: TopDonor[] }> {
    try {
      const response = await api.get(`/admin/donations/campaigns/${campaignId}/recent-donors${limit ? `?limit=${limit}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Get recent donors error:', error);
      throw error;
    }
  }

  // ============================================
  // CAMPAIGN FEATURES (Admin)
  // ============================================

  async featureCampaign(id: string, data: {
    destaque?: boolean;
    trending?: boolean;
    verificada?: boolean;
  }): Promise<{ message: string }> {
    try {
      const response = await api.post(`/admin/donations/campaigns/${id}/feature`, data);
      return response.data;
    } catch (error) {
      console.error('Feature campaign error:', error);
      throw error;
    }
  }

  // ============================================
  // MEDIA (GoFundMe)
  // ============================================

  async getCampaignMedia(campaignId: string): Promise<{ data: CampaignMedia[] }> {
    try {
      const response = await api.get(`/admin/donations/campaigns/${campaignId}/media`);
      return response.data;
    } catch (error) {
      console.error('Get campaign media error:', error);
      throw error;
    }
  }

  async addCampaignMedia(campaignId: string, data: Partial<CampaignMedia>): Promise<{ id: string; message: string }> {
    try {
      const response = await api.post(`/admin/donations/campaigns/${campaignId}/media`, data);
      return response.data;
    } catch (error) {
      console.error('Add campaign media error:', error);
      throw error;
    }
  }

  async deleteCampaignMedia(id: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/admin/donations/media/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete campaign media error:', error);
      throw error;
    }
  }
}

export default new DonationsService();
